import { useMutation, useQueryClient } from "@tanstack/react-query";
import { facilityApi, isApiError } from "@bw/shared/api";
import type {
  BatchBookingRequest,
  BatchBookingResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The CHARGE: book N cart slots under one Stripe charge
 * (POST /facilities/{id}/bookings/batch, #1313).
 *
 * The request carries the chosen stripe_payment_method_id + a fresh
 * idempotency_key (the PAGE owns key generation/regeneration — a fresh uuid per
 * genuinely separate attempt; stable only for an in-flight retry of the
 * identical request). This hook never invents one.
 *
 * Errors are NOT thrown to a boundary — they surface on `mutation.error` for the
 * page to map by status: 428 waiver, 409 conflict/mixed-currency (the whole cart
 * rolled back + any charge refunded — NEVER a partial success), 402/422 declined,
 * 403 auth. On a 409 the affected dates' availability is invalidated here so the
 * court sheet reflects the lost slot when the player returns to re-select.
 *
 * Success → BatchBookingResponse (bookings + recurring_group_id + total_charged +
 * currency). The page clears the cart and shows the confirmation; it does NOT
 * re-quote or re-charge to render it.
 */
export function useBatchBooking(facilityId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<BatchBookingResponse, unknown, BatchBookingRequest>({
    mutationFn: (data: BatchBookingRequest) =>
      facilityApi.batchCreateBooking(facilityId as string, data),
    onSuccess: () => {
      // The booked dates are no longer available — refresh any cached day.
      void queryClient.invalidateQueries({
        queryKey: ["booking", "availability", facilityId ?? ""],
      });
    },
    onError: (error, variables) => {
      // A 409 means a slot was taken (or a mixed-currency cart) and the whole
      // cart rolled back. Re-fetch availability for the affected dates so the
      // sheet shows the lost slot; the page routes the user back to re-select.
      if (isApiError(error) && error.status === 409) {
        const dates = new Set(
          variables.slots.map((s) => s.start_time.slice(0, 10)),
        );
        for (const date of dates) {
          void facilityApi.getAvailability(facilityId as string, date);
          void queryClient.invalidateQueries({
            queryKey: queryKeys.booking.availability(facilityId ?? "", date),
          });
        }
      }
    },
  });
}
